/**
 * ConfirmationModal — simple 2-button modal for destructive actions.
 *
 * Used for batch-print confirmation in Riwayat.
 *
 * Built on RN's Modal component (no @gorhom/bottom-sheet dep needed for
 * this simple centered dialog). Pressable backdrop to dismiss.
 */

import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';

import { Brand, Radii, Spacing } from '@/constants/theme';

interface ConfirmationModalProps {
  visible: boolean;
  title: string;
  body: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmationModal({
  visible,
  title,
  body,
  confirmLabel = 'Lanjut',
  cancelLabel = 'Batal',
  destructive = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}: ConfirmationModalProps) {
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
    >
      <Pressable style={styles.backdrop} onPress={onCancel}>
        {/* Inner Pressable prevents the backdrop tap from firing on the card. */}
        <Pressable style={styles.card} onPress={(e) => e.stopPropagation?.()}>
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.body}>{body}</Text>
          <View style={styles.actions}>
            <Pressable
              onPress={onCancel}
              style={({ pressed }) => [styles.btn, styles.btnCancel, pressed && styles.btnPressed]}
              accessibilityRole="button"
              accessibilityLabel={cancelLabel}
            >
              <Text style={styles.btnCancelText}>{cancelLabel}</Text>
            </Pressable>
            <Pressable
              onPress={onConfirm}
              disabled={confirmDisabled}
              style={({ pressed }) => [
                styles.btn,
                destructive ? styles.btnDestructive : styles.btnPrimary,
                confirmDisabled && styles.btnDisabled,
                pressed && styles.btnPressed,
              ]}
              accessibilityRole="button"
              accessibilityLabel={confirmLabel}
              accessibilityState={{ disabled: confirmDisabled }}
            >
              <Text style={styles.btnPrimaryText}>{confirmLabel}</Text>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: Spacing.four,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: Radii.medium,
    padding: Spacing.four,
    width: '100%',
    maxWidth: 360,
    gap: Spacing.three,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1A0B5C',
  },
  body: {
    fontSize: 14,
    lineHeight: 20,
    color: '#4b5563',
  },
  actions: {
    flexDirection: 'row',
    gap: Spacing.two,
    marginTop: Spacing.two,
  },
  btn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: Radii.small,
    alignItems: 'center',
  },
  btnCancel: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e5e5',
  },
  btnCancelText: {
    color: '#4b5563',
    fontSize: 14,
    fontWeight: '600',
  },
  btnPrimary: {
    backgroundColor: Brand.primary,
  },
  btnDestructive: {
    backgroundColor: '#e53e3e',
  },
  btnDisabled: {
    opacity: 0.55,
  },
  btnPrimaryText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '700',
  },
  btnPressed: {
    opacity: 0.7,
  },
});
